/**
 * Auth Store - Zustand store for authentication state
 * Manages JWT tokens, login/logout, and session state
 */

import { STORAGE_KEYS } from "@lib/config/storageKeys";
import { jwtDecode } from "jwt-decode";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { queryClient } from "@/main";
import { usePermissionStore } from "../stores/permissions.store";

interface JwtPayload {
  readonly id?: number;
  readonly roles?: string[];
  readonly exp?: number;
  readonly iat?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
}

export interface AuthState {
  token: string | null;
  refreshToken: string | null;
  qrCodeToken: string | null;
  isLoggedIn: boolean;
  roles: string[];
  userId: number | null;
  email?: string;
  prenom?: string;
  nom?: string;
  avatar?: { id: number; storagePath?: string } | null;
}

export interface AuthActions {
  login: (token: string, refreshToken?: string) => void;
  loginQrCode: (token: string) => void;
  logout: () => void;
  logoutQrCode: () => void;
  setUserId: (userId: number) => void;
}

export type AuthStore = AuthState & AuthActions;

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      token: null,
      refreshToken: null,
      qrCodeToken: null,
      isLoggedIn: false,
      roles: [],
      userId: null,

      login: (token, refreshToken) => {
        const decoded = jwtDecode<JwtPayload>(token);

        set({
          token,
          refreshToken: refreshToken ?? null,
          isLoggedIn: true,
          roles: decoded.roles || [],
          userId: decoded.id || null,
          email: decoded.email,
          prenom: decoded.prenom,
          nom: decoded.nom,
        });
      },

      loginQrCode: (token) => {
        set({ qrCodeToken: token });
      },

      logout: () => {
        set({
          token: null,
          refreshToken: null,
          qrCodeToken: null,
          isLoggedIn: false,
          roles: [],
          userId: null,
        });
        // Clear permissions on logout
        usePermissionStore.getState().clear();
        // Invalidate all TanStack Query caches so next login gets fresh data
        queryClient.clear();
      },

      logoutQrCode: () => {
        set({ qrCodeToken: null });
      },

      setUserId: (userId) => set({ userId }),
    }),
    {
      name: STORAGE_KEYS.AUTH.TOKEN,
      storage: createJSONStorage(() => localStorage),
    },
  ),
);

/**
 * Synchronous read from localStorage for auth state
 * Used in loaderDeps (runs before beforeLoad, can't use Zustand async rehydrate)
 */
export function readAuthFromStorage(): {
  userId: number | null;
  token: string | null;
} {
  try {
    const stored = localStorage.getItem(STORAGE_KEYS.AUTH.TOKEN);
    if (!stored) return { userId: null, token: null };

    const parsed = JSON.parse(stored);
    // Zustand persist wraps state in { state: {...}, version: N }
    const state = parsed?.state ?? parsed;

    return {
      userId: state?.userId ?? null,
      token: state?.token ?? null,
    };
  } catch {
    return { userId: null, token: null };
  }
}

// Re-export for backward compatibility
export default useAuthStore;
